import classNames from 'classnames';
import React, { ReactNode, useState } from 'react';
import { Logo } from '../../../../layout/BotBuilderSidebar/Logo/Logo';
import styles from './CustomerQualificationFlow.module.css';
import { Iconiq } from '../LoginLayout/BlueLayout/Iconiq';
import { Inc500 } from '../LoginLayout/BlueLayout/Inc500';
import { G2BadgeTop50 } from '@mm/icons/general/G2BadgeTop50';
import { ReactComponent as LeaderBadge } from 'assets/leader-2025.svg';
import { MinusOutlined } from '@ant-design/icons';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { useSelector } from 'react-redux';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import { OnboardingRoutes } from '../../../../routes/onboarding-routes';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { useHistory } from 'react-router-dom';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { Button } from '@mm/components';
import { Routes } from '../../../../routes/top-level-routes';
import {
  adSpendOptions,
  agencyPartnership,
  agencyServicesOptions,
  agencyWebsiteVisitorsOptions,
  getQualificationFlowDescription,
  influencers,
  isUSAgency,
  websiteVisitorsOptions,
} from './constants';
import { Select } from 'antd';
import { useSetNextOnboardingStep } from '../../../../dashboard/pages/SignupOnboarding/use-set-next-onboarding-step';

export type Influencer = {
  name?: string;
  companyLogo?: string;
  role?: string;
  quote?: string;
  photo?: string;
};

export const CustomerQualificationLayout = ({
  children,
  step,
}: {
  children: ReactNode;
  step: number;
}) => {
  const activeCompany = useSelector(activeCompanySelector);
  const { userAttributes } = useSelector(selectSessionSelector) || {};
  const [error, setError] = useState(false);
  const [values, setValues] = useState<Record<string, string | string[]>>({});
  const [busy, setBusy] = useState(false);

  const isAgency = Boolean(
    activeCompany?.businessType === 'agency' ||
      activeCompany?.companyAttributes?.agency ||
      userAttributes?.agency,
  );

  const requiredFields = isAgency
    ? ['websiteVisitors', 'isUSAgency', 'agencyPartnership', 'services']
    : ['monthlyAdSpend', 'websiteVisitors'];
  const submitDisabled = !requiredFields.every(field =>
    Array.isArray(values[field]) ? values[field].length : values[field],
  );

  const { update } = useCompanyCRUDActions();
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const history = useHistory();
  const config = getQualificationFlowDescription(isAgency);

  const cleanedValues = (obj: Object) =>
    Object.fromEntries(Object.entries(obj).filter(([_, v]) => v));

  const updateCompany = async () => {
    if (activeCompany) {
      const { id } = activeCompany;

      if (!submitDisabled) {
        setBusy(true);
        const res = await update(
          {
            id,
            ...cleanedValues({
              monthlyAdSpend: values.monthlyAdSpend,
              websiteVisitors: values.websiteVisitors,
            }),
            companyAttributes: cleanedValues({
              isUsAgency: values.isUSAgency === 'true' ? true : false,
              agencyPartnership: values.agencyPartnership,
              services: values.services,
            }),
          },
          'full=true',
        );
        setNextOnboardingStep(
          res?.company.consideredQualified
            ? OnboardingSignupStep.BOOK_DEMO
            : OnboardingSignupStep.NONE,
        );

        setBusy(false);

        if (res?.company.consideredQualified) {
          history.push(OnboardingRoutes.bookDemo.path);
        } else {
          history.push(Routes.chatbotEditor.path);
        }
      }
    }
    setError(true);
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.whiteContainer]: step === 3,
      })}
    >
      <div
        className={classNames(styles.leftSide, {
          [styles.thankYouLeftSide]: step === 3,
        })}
      >
        <Logo className={styles.logo} />
        {step !== 3 && (
          <>
            <div className={styles.steps}>
              <span
                className={classNames(styles.step, {
                  [styles.activeStep]: step === 1,
                })}
              >
                Step 1
              </span>
              <MinusOutlined className={styles.tail} />
              <span
                className={classNames(styles.step, {
                  [styles.activeStep]: step === 2,
                })}
              >
                Step 2
              </span>
            </div>
            {config.map(c => (
              <div className={styles.formHeader} key={c[step]}>
                <div className={styles.title}>{c[step].title}</div>
                <div className={styles.subTitle}>{c[step].description}</div>
              </div>
            ))}
          </>
        )}

        {step === 1 && children}
        {step === 2 && (
          <div className={styles.form}>
            {!isAgency && (
              <>
                <div className={styles.inputGroup}>
                  <label
                    className={classNames(styles.formLabel, {
                      [styles.formLabelInvalid]:
                        error && !values.websiteVisitors,
                    })}
                  >
                    Monthly U.S. website visitors
                  </label>
                  <Select
                    className={classNames(styles.formSelect)}
                    value={values?.websiteVisitors}
                    onChange={v => setValues({ ...values, websiteVisitors: v })}
                    options={websiteVisitorsOptions}
                    placeholder="Choose"
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label
                    className={classNames(styles.formLabel, {
                      [styles.formLabelInvalid]:
                        error && !values.monthlyAdSpend,
                    })}
                  >
                    Monthly ad spend
                  </label>
                  <Select
                    className={classNames(styles.formSelect)}
                    value={values?.monthlyAdSpend}
                    onChange={v => setValues({ ...values, monthlyAdSpend: v })}
                    options={adSpendOptions}
                    placeholder="Choose"
                  />
                </div>
              </>
            )}

            {isAgency && (
              <>
                <div className={styles.inputGroup}>
                  <label
                    className={classNames(styles.formLabel, {
                      [styles.formLabelInvalid]: error && !values.isUSAgency,
                    })}
                  >
                    Does your agency serve companies in the U.S. market?
                  </label>
                  <Select
                    className={classNames(styles.formSelect)}
                    value={values.isUSAgency}
                    onChange={v => setValues({ ...values, isUSAgency: v })}
                    options={isUSAgency}
                    placeholder="Choose"
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label
                    className={classNames(styles.formLabel, {
                      [styles.formLabelInvalid]:
                        error && !values.agencyPartnership,
                    })}
                  >
                    How do you typically work with Technology Partners?
                  </label>
                  <Select
                    className={classNames(styles.formSelect)}
                    value={values.agencyPartnership}
                    onChange={v =>
                      setValues({ ...values, agencyPartnership: v })
                    }
                    options={agencyPartnership}
                    placeholder="Choose"
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label
                    className={classNames(styles.formLabel, {
                      [styles.formLabelInvalid]:
                        error && !values.websiteVisitors,
                    })}
                  >
                    How much U.S. website traffic do the clients you work with
                    generate today (unique U.S. monthly visitors)?
                  </label>
                  <Select
                    className={classNames(styles.formSelect)}
                    value={values.websiteVisitors}
                    onChange={v => setValues({ ...values, websiteVisitors: v })}
                    options={agencyWebsiteVisitorsOptions}
                    placeholder="Choose"
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label
                    className={classNames(styles.formLabel, {
                      [styles.formLabelInvalid]:
                        error && !values.services.length,
                    })}
                  >
                    What Services do you provide to your clients?{' '}
                    <span>Check all that apply</span>
                  </label>
                  <Select
                    className={classNames(styles.formSelect)}
                    value={values.services}
                    onChange={v => setValues({ ...values, services: v })}
                    options={agencyServicesOptions}
                    placeholder="Choose"
                    mode="multiple"
                  />
                </div>
              </>
            )}

            <Button
              css={{ container: styles.submitBtn }}
              disabled={submitDisabled || busy}
              onClick={updateCompany}
            >
              Book a Call
            </Button>
          </div>
        )}
      </div>
      <div className={styles.rightSide}>
        {influencers[step].map((influencer: Influencer) => (
          <div className={styles.partner} key={influencer.name}>
            <img
              src={influencer.photo}
              alt={influencer.name}
              className={styles.partnerImg}
            />
            <div className={styles.partnerDescription}>
              <div>
                {influencer.name && (
                  <div className={styles.name}>{influencer.name}</div>
                )}
                {influencer.role && (
                  <div className={styles.role}>{influencer.role}</div>
                )}
              </div>
              <img
                src={influencer.companyLogo}
                alt="Logo"
                className={styles.partnerLogo}
              />
              <div className={styles.quote}>{influencer.quote}</div>
            </div>
          </div>
        ))}
        <div className={styles.badges}>
          <Iconiq />
          <LeaderBadge />
          <G2BadgeTop50 />
          <Inc500 style={{ marginLeft: '-10px' }} />
        </div>
      </div>
    </div>
  );
};
